import { Activity, Attempt } from '@/common/types/session';
import getActivityContext from '@/common/utils/activityContext';
import { mapActivityToSkill, sanitize } from '@/common/utils/helpers';
import api from '@/db/apiClient';
import dayjs from 'dayjs';

interface ErrorPair {
  correctAnswer: string;
  studentAnswer: string;
  count: number;
}

interface ActivityStats {
  activity: Activity;
  displayName: string;
  skill: string;
  context: string;
  correct: number;
  total: number;
  accuracy: number;
  errors: ErrorPair[];
}

function isCorrect(attempt: Attempt): boolean {
  return sanitize(attempt.correctAnswer) === sanitize(attempt.studentAnswer);
}

function parseContext(activity: Activity): string {
  if ('context' in activity && activity.context) {
    try {
      const parsed = JSON.parse(activity.context);
      if (parsed.level) {
        return getActivityContext(activity.activityName, parsed.level);
      }
    } catch {
      // context is not JSON, use as-is
      return activity.context;
    }
  }
  if ('level' in activity && activity.level) {
    return getActivityContext(activity.activityName, activity.level);
  }
  return '';
}

function computeErrors(attempts: Attempt[]): ErrorPair[] {
  const errorMap = new Map<string, ErrorPair>();

  for (const attempt of attempts) {
    if (!isCorrect(attempt)) {
      const key = `${sanitize(attempt.correctAnswer)}|${sanitize(attempt.studentAnswer)}`;
      const existing = errorMap.get(key);
      if (existing) {
        existing.count++;
      } else {
        errorMap.set(key, {
          correctAnswer: attempt.correctAnswer,
          studentAnswer: attempt.studentAnswer,
          count: 1,
        });
      }
    }
  }

  return Array.from(errorMap.values()).sort((a, b) => b.count - a.count);
}

function formatForDisplay(text: string): string {
  if (!text || !text.includes(' ')) return text;
  // Capitalize first letter and add period for sentences
  let formatted = text.charAt(0).toUpperCase() + text.slice(1);
  if (!/[.!?]$/.test(formatted)) {
    formatted += '.';
  }
  return formatted;
}

function accuracyColor(accuracy: number): string {
  if (accuracy >= 80) return '#d9ead3'; // green
  if (accuracy >= 50) return '#fff2cc'; // yellow
  return '#fecaca'; // red
}

export async function generateSessionSummaryHTML(
  organisationId: string,
  session: { id: string; startedAt: number },
  activities: Activity[],
): Promise<string> {
  // Fetch attempts for all activities in parallel
  const attemptsPerActivity = await Promise.all(
    activities.map((activity) =>
      api.asEducator.getAttemptsForActivity(organisationId, activity.id),
    ),
  );

  // Compute per-activity stats
  const activityStats: ActivityStats[] = activities.map((activity, i) => {
    const attempts = attemptsPerActivity[i];
    const correct = attempts.filter(isCorrect).length;
    const total = attempts.length;
    const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
    const skillInfo = mapActivityToSkill(activity.activityName);
    const context = parseContext(activity);

    return {
      activity,
      displayName: skillInfo?.CleanName ?? activity.activityName,
      skill: skillInfo?.Skill ?? '',
      context,
      correct,
      total,
      accuracy,
      errors: computeErrors(attempts),
    };
  });

  // Compute duration
  const lastActivity = activities.reduce(
    (latest, a) => {
      const endTime = a.endedAt ?? a.startedAt;
      return endTime > latest ? endTime : latest;
    },
    session.startedAt,
  );
  const durationMinutes = Math.round((lastActivity - session.startedAt) / 60000);

  // Build HTML
  let html = '';

  html += `<h1>Session Summary</h1>`;
  html += `<p>${dayjs(session.startedAt).format('DD/MM/YYYY h:mm a')} | Duration: ${durationMinutes}m | Activities: ${activities.length}</p>`;

  html += `<h2>Activity Breakdown</h2>`;

  for (const stats of activityStats) {
    html += `<h3>${stats.displayName}</h3>`;
    html += `<ul>`;
    if (stats.skill) {
      html += `<li><strong>Skill:</strong> ${stats.skill}</li>`;
    }
    if (stats.context) {
      html += `<li>${stats.context}</li>`;
    }
    html += `<li><strong>Accuracy:</strong> <mark style="background-color: ${accuracyColor(stats.accuracy)}">${stats.accuracy}%</mark> (${stats.correct}/${stats.total})</li>`;
    if (stats.errors.length > 0) {
      html += `<li><span style="color: #cc0000">Errors:</span>`;
      html += `<ul>`;
      for (const error of stats.errors) {
        const countStr = error.count > 1 ? ` <span style="color: #999">(x${error.count})</span>` : '';
        html += `<li><span style="color: #cc0000">${error.studentAnswer}</span> <span style="color: #6aa84f">(${formatForDisplay(error.correctAnswer)})</span>${countStr}</li>`;
      }
      html += `</ul>`;
      html += `</li>`;
    }
    html += `</ul>`;
  }

  html += `<h2>Notes</h2>`;
  html += `<p></p>`;

  return html;
}
